import { addHours } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CdkDragEnter } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ISnListQueryParameters } from '@lib/api';
import { FsMultiDragDrop } from '@lib/common/services';
import { SnCamera, SnCameraDisposition } from 'libs/api/entities/src/lib/camera';
import { SnFloor, SnLocation } from 'libs/api/entities/src/lib/location';
import { SnCamerasAPI } from 'libs/api/services/src/lib/cameras.service';
import { SnLocationsAPI } from 'libs/api/services/src/lib/locations.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'fs-place-cameras-dialog',
  templateUrl: './place-cameras-dialog.component.html',
  styleUrls: ['./place-cameras-dialog.component.scss'],
})
export class PlaceCamerasDialogComponent implements OnDestroy, OnInit {
  public cameras: SnCamera[] = [];
  public activeFloor: SnFloor;
  public activeFloorId: string = 'null';
  public currentTime = new Date().getTime();
  public timeShift;
  public isLoadingCameras: boolean = false;
  public cameraSearched: string;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public location: SnLocation,
    private readonly _cameraService: SnCamerasAPI,
    private readonly _locationService: SnLocationsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _matDialogRef: MatDialogRef<PlaceCamerasDialogComponent>,
    private readonly _multiDragDrop: FsMultiDragDrop,
  ) {}

  public ngOnInit(): void {
    this._fetchCameras();
    const time = addHours(this.currentTime, -1);
    this.timeShift = Math.floor(time.getTime() / 1000) - 30;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setFloor(floor?: SnFloor): void {
    if (floor) {
      this.activeFloor = floor;
      this.activeFloorId = floor.id;
    } else {
      this.activeFloor = null;
      this.activeFloorId = 'null';
    }
    this._fetchCameras();
  }

  public setMultiselect(id: string): void {
    const element = document.getElementById(id);
    this._multiDragDrop.addToMultiSelect(element);
  }

  public dragReleased(event: CdkDragEnter): void {
    const ids = this._multiDragDrop.dragReleased();
    if (ids.length) {
      ids.forEach((cameraId) => {
        if (event.container?.id) {
          this.addToFloor(cameraId, event.container.id);
        }
      });
    }

    if (event.item.element?.nativeElement?.id && event.container.id) {
      this.addToFloor(event.item.element?.nativeElement?.id, event.container.id);
    }
  }

  public addToFloor(cameraId: string, floorId: string): void {
    if (floorId === 'unassigned') {
      floorId = '';
    }
    this._cameraService
      .updateCamera(cameraId, {
        floor: floorId,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        const index = this.cameras.findIndex((camera) => {
          return camera.id === res.id;
        });

        if (index > -1) {
          this.cameras.splice(index, 1);
        }
        this._cd.detectChanges();
      });
  }

  public getThumbnail(camera: SnCamera): string {
    if (camera?.status === 'Up' && camera?.playbackUrls) {
      if (camera?.playbackUrls?.main.local?.mp4thumbnail) {
        return camera?.playbackUrls?.main.local?.mp4thumbnail
          .split('<utc>.mp4')
          .join(`${this.timeShift}.mp4`);
      } else if (camera.playbackUrls?.main.cloud?.mp4thumbnail) {
        return camera?.playbackUrls?.main.cloud?.mp4thumbnail
          .split('<utc>.mp4')
          .join(`${this.timeShift}.mp4`);
      }
    }
  }

  public onSearchValueChange(filterValue: string): void {
    this.cameraSearched = filterValue;
    this._fetchCameras();
  }

  public toggleDispositionType(event: any, camera: SnCamera): void {
    if (event.checked) {
      camera.disposition = SnCameraDisposition.Internal;
    }
    if (!event.checked) {
      camera.disposition = SnCameraDisposition.External;
    }
    const dispositionParam = camera.disposition;

    this._cameraService
      .updateCamera(camera.id, {
        disposition: dispositionParam,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public close(): void {
    this._matDialogRef.close();
  }

  private _fetchCameras(params?: ISnListQueryParameters): void {
    if (this.cameraSearched) {
      params.search = this.cameraSearched;
    }
    this.isLoadingCameras = true;
    this._locationService
      .getLocationCameraList(this.location.id, this._prepareQueryParams(params))
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this.cameras = res.results;
        this._setThumbnails();
        this.isLoadingCameras = false;
      });
  }

  private _setThumbnails(): void {
    this.cameras.forEach((camera) => {
      camera.cameraMeta.thumbnail = this.getThumbnail(camera);
    });
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      pageSize: 'all',
      floor: this.activeFloorId,
      ...params,
    };
  }
}
